/**
 * audioPresets.ts
 * =============================================================================
 * Configuration presets for each audio mode (Focus, Sleep, Anxiety).
 * Defines binaural frequencies, recommended volumes, and audio file mappings.
 * 
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

import type { 
  AudioMode, 
  BinauralConfig, 
  VolumeState,
  AudioFileMetadata 
} from '../core/types';

// =============================================================================
// BINAURAL PRESETS
// =============================================================================

/**
 * Binaural beat configurations per mode
 * Based on neuroscience research documented in project files
 */
export const BINAURAL_PRESETS: Record<AudioMode, BinauralConfig> = {
  /**
   * Focus Mode (Deep Work)
   * Beta waves (14-18Hz) for sustained attention
   * Reference: Brain.fm research on beta-rate modulation
   */
  focus: {
    carrierFrequency: 200,    // Hz - Low enough to not be distracting
    beatFrequency: 16,         // Hz - Beta range for concentration
    waveform: 'sine',
  },
  
  /**
   * Sleep Mode (Recovery)
   * Delta waves (0.5-4Hz) for deep sleep induction
   * Reference: Sleep and Circadian Regulation research
   */
  sleep: {
    carrierFrequency: 150,    // Hz - Lower carrier for relaxation
    beatFrequency: 2,          // Hz - Delta range for NREM sleep
    waveform: 'sine',
  },
  
  /**
   * Anxiety Reset Mode (Regulation)
   * Alpha waves (8-12Hz) for relaxed alertness
   * Reference: EMDR bilateral stimulation research
   */
  anxiety: {
    carrierFrequency: 180,    // Hz - Moderate carrier
    beatFrequency: 10,         // Hz - Alpha range for calm focus
    waveform: 'sine',
  },
};

// =============================================================================
// VOLUME PRESETS
// =============================================================================

/**
 * Recommended volume levels per mode
 * These create the optimal balance for each use case
 */
export const VOLUME_PRESETS: Record<AudioMode, VolumeState> = {
  focus: {
    master: 0.7,
    ambience: 0.6,    // Moderate - masks distractions
    tone: 0.25,       // Subtle - present but not distracting
    voice: 0.0,       // Off by default for focus
  },
  
  sleep: {
    master: 0.5,      // Lower overall volume for sleep
    ambience: 0.7,    // Higher ambience for immersion
    tone: 0.2,        // Very subtle binaural
    voice: 0.0,       // Off - or used briefly for wind-down
  },
  
  anxiety: {
    master: 0.6,
    ambience: 0.5,    // Moderate ambience
    tone: 0.3,        // Slightly more prominent for regulation
    voice: 0.8,       // Higher for grounding guide
  },
};

// =============================================================================
// AUDIO LIBRARY (Placeholder URLs)
// =============================================================================

/**
 * Audio file library organized by mode
 * URLs point to /public/audio/ directory
 * 
 * NOTE: Replace these placeholder paths with your actual recorded files
 */
export const AUDIO_LIBRARY: Record<AudioMode, {
  ambience: AudioFileMetadata[];
  voice: AudioFileMetadata[];
}> = {
  focus: {
    ambience: [
      {
        id: 'brown-noise-organic',
        name: 'Ruido Marrón Orgánico',
        description: 'Ruido marrón orgánico de alta fidelidad para concentración',
        duration: 120, // Duración en segundos
        format: 'ogg',
        url: '/audio/ambience/organic_brown.webm',
        category: 'ambience',
        mode: 'focus',
        tags: ['noise', 'brown', 'organic'],
      },
      {
        id: 'library-rain',
        name: 'Biblioteca con Lluvia',
        description: 'Ambiente de biblioteca con lluvia suave exterior',
        duration: 1800,
        format: 'ogg',
        url: '/audio/ambience/library-rain.webm',
        category: 'ambience',
        mode: 'focus',
        tags: ['library', 'rain', 'indoor'],
      },
    ],
    voice: [], // No voice guide for focus mode
  },
  
  sleep: {
    ambience: [
      {
        id: 'santo-domingo',
        name: 'Santo Domingo Nocturno',
        description: 'Río y fauna nocturna tropical para sueño profundo',
        duration: 120, // Pon aquí la duración real en segundos (ej. 120 para 2 min)
        format: 'ogg',
        url: '/audio/ambience/santo_domingo.webm', // <--- La ruta exacta
        category: 'ambience',
        mode: 'sleep',
        tags: ['ecuador', 'santo domingo', 'river', 'night', 'nature'],
        location: 'Santo Domingo, Ecuador',
      },
      {
        id: 'yasuni-rainforest',
        name: 'Selva del Yasuní',
        description: 'Sonidos nocturnos de la selva amazónica',
        duration: 2700,
        format: 'ogg',
        url: '/audio/ambience/yasuni-rainforest.webm',
        category: 'ambience',
        mode: 'sleep',
        tags: ['ecuador', 'amazon', 'rainforest', 'night'],
        location: 'Parque Nacional Yasuní, Ecuador',
      },
      {
        id: 'ocean-waves-coast',
        name: 'Oleaje de Costa',
        description: 'Olas suaves de la costa ecuatoriana',
        duration: 2700,
        format: 'ogg',
        url: '/audio/ambience/ocean-waves-coast.webm',
        category: 'ambience',
        mode: 'sleep',
        tags: ['ecuador', 'coast', 'ocean', 'waves'],
        location: 'Costa del Pacífico, Ecuador',
      },
    ],
    voice: [], // No voice guide for sleep mode
  },

  anxiety: {
    ambience: [
      {
        id: 'ambience-river-reset',
        name: 'Río Blanco',
        description: 'Río de montaña para acompañar el reset de ansiedad',
        duration: 0, // Gapless loop - exact duration not required
        format: 'ogg' as const,
        url: '/audio/ambience/river_reset.webm',
        category: 'ambience' as const,
        mode: 'anxiety' as const,
        tags: ['river', 'nature', 'reset'],
      },
    ],
    voice: [
      {
        id: 'grounding-guide',
        name: 'Guía de Grounding',
        description: 'Ejercicio de toma de tierra de 3 minutos',
        duration: 180,
        format: 'ogg',
        url: '/audio/voice/grounding-guide-es.webm',
        category: 'voice',
        mode: 'anxiety',
        tags: ['guide', 'spanish', 'grounding', 'anxiety'],
      },
      {
        id: 'breathing-guide',
        name: 'Guía de Respiración 4-7-8',
        description: 'Ejercicio de respiración para calma',
        duration: 240,
        format: 'ogg',
        url: '/audio/voice/breathing-guide-es.webm',
        category: 'voice',
        mode: 'anxiety',
        tags: ['guide', 'spanish', 'breathing', 'anxiety'],
      },
    ],
  },
};

// =============================================================================
// MODE CONFIGURATIONS
// =============================================================================

/**
 * Complete configuration for each audio mode
 */
export interface ModeConfig {
  id: AudioMode;
  name: string;
  description: string;
  icon: string;
  color: string;
  binaural: BinauralConfig; // Kept for reference/documentation only (not used by engine v2.8+)
  /**
   * [v2.8] File-based entrainment track URL (loops with 3s fade-in).
   * Null = no entrainment file for this mode (silence on tone layer).
   */
  entrainmentUrl: string | null;
  /**
   * [v2.9] Optional one-shot "Pavlovian trigger" sound (e.g. Tibetan bowl).
   * Plays exactly once when the mode starts. The audio file provides its own
   * natural envelope - no fade-in or fade-out is applied.
   * Undefined = no trigger for this mode.
   */
  triggerUrl?: string;
  /**
   * Optional voice guide URL for this mode.
   * When set, this path is used directly instead of falling back to
   * AUDIO_LIBRARY. Undefined = no default voice guide for this mode.
   */
  voiceUrl?: string;
  /**
   * Default ambience fader volume (0-100) for this mode.
   * Compensates for spectral density differences between ambience tracks
   * (e.g., dense brown noise vs. sparse river sounds).
   * Applied automatically when the user switches to this mode.
   */
  defaultAmbienceVolume: number;
  defaultVolumes: VolumeState;
  timerDefault: number; // minutes
  features: string[];
}

export const MODE_CONFIGS: Record<AudioMode, ModeConfig> = {
  focus: {
    id: 'focus',
    name: 'Enfoque Profundo',
    description: 'Ruido marrón esculpido + modulación isocrónica Beta para trabajo profundo.',
    icon: '🎯',
    color: '#3B82F6', // Blue
    binaural: BINAURAL_PRESETS.focus, // Reference only
    entrainmentUrl: '/audio/tones/focus_pad.webm', // [v2.8] Functional music - Opus codec for gapless looping
    defaultAmbienceVolume: 50, // Dense brown noise — moderate presence
    defaultVolumes: VOLUME_PRESETS.focus,
    timerDefault: 25, // Pomodoro
    features: [
      'Ruido marrón de alta fidelidad',
      'Música funcional Beta (14Hz isocrónico)',
      'Timer Pomodoro integrado',
      'Sin distracciones tonales',
    ],
  },

  sleep: {
    id: 'sleep',
    name: 'Sueño Reparador',
    description: 'Paisajes sonoros documentales (Ecuador) + arrastre Delta para inducir fase NREM.',
    icon: '🌙',
    color: '#8B5CF6', // Purple
    binaural: BINAURAL_PRESETS.sleep,
    entrainmentUrl: '/audio/tones/sleep_pad.webm', // [v2.9] Delta wave drone, loops with fade-in
    triggerUrl: '/audio/tones/sleep_trigger.webm', // [v2.9] Tibetan bowl one-shot anchor
    defaultAmbienceVolume: 35, // Nature soundscape — subtle bed underneath drone
    defaultVolumes: VOLUME_PRESETS.sleep,
    timerDefault: 90, // Ultradian cycle
    features: [
      'Grabaciones de campo de Ecuador',
      'Drone Delta (2Hz) para sueño profundo',
      'Campana tibetana de anclaje pavloviano',
      'Timer de 90 min (ciclo ultradiano)',
    ],
  },

  anxiety: {
    id: 'anxiety',
    name: 'Reset de Ansiedad',
    description: 'Estimulación bilateral + guía respiratoria clínica para desactivación de la amígdala.',
    icon: '🧘',
    color: '#10B981', // Green
    binaural: BINAURAL_PRESETS.anxiety,
    entrainmentUrl: '/audio/tones/reset_pad.webm', // [v2.9] Alpha pulse pad, loops with fade-in
    triggerUrl: '/audio/tones/reset_trigger.webm', // [v2.9] Grounding tone one-shot anchor
    voiceUrl: '/audio/voice/voice_reset.webm',     // Grounding voice guide for anxiety reset
    defaultAmbienceVolume: 15, // Sparse river — quiet backdrop for voice + pad
    defaultVolumes: VOLUME_PRESETS.anxiety,
    timerDefault: 15, // Short session
    features: [
      'Guía de Grounding (3 min)',
      'Pulsos Alpha (10Hz) para calma',
      'Drones armónicos estables',
      'Estimulación bilateral (panning)',
    ],
  },
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * [v2.8] Get the entrainment URL for a mode, if one exists.
 * Returns null for modes that have not yet had a file composed.
 */
export function getEntrainmentUrl(mode: AudioMode): string | null {
  return MODE_CONFIGS[mode].entrainmentUrl;
}

/**
 * [v2.9] Get the one-shot trigger URL for a mode, if one exists.
 * Returns undefined for modes without a Pavlovian trigger.
 */
export function getTriggerUrl(mode: AudioMode): string | undefined {
  return MODE_CONFIGS[mode].triggerUrl;
}

/**
 * Get the first available ambience file for a mode
 */
export function getDefaultAmbience(mode: AudioMode): AudioFileMetadata | null {
  const files = AUDIO_LIBRARY[mode].ambience;
  return files.length > 0 ? files[0] : null;
}

/**
 * Get the voice guide URL for a mode, if one exists.
 * Checks MODE_CONFIGS.voiceUrl first (direct path), then falls back to
 * the first entry in AUDIO_LIBRARY for backwards compatibility.
 */
export function getVoiceUrl(mode: AudioMode): string | undefined {
  // Prefer the explicit voiceUrl on the mode config (same pattern as entrainmentUrl)
  const configUrl = MODE_CONFIGS[mode].voiceUrl;
  if (configUrl) return configUrl;

  // Fall back to the AUDIO_LIBRARY for modes that still use the old metadata array
  const files = AUDIO_LIBRARY[mode].voice;
  return files.length > 0 ? files[0].url : undefined;
}

/**
 * Get the first available voice guide for a mode.
 * Returns an AudioFileMetadata-compatible object so callers don't need to change.
 * Delegates to getVoiceUrl() so MODE_CONFIGS.voiceUrl is always respected.
 */
export function getDefaultVoice(mode: AudioMode): AudioFileMetadata | null {
  // Check for a direct voiceUrl on the config first
  const url = getVoiceUrl(mode);
  if (!url) return null;

  // If the mode has full metadata in AUDIO_LIBRARY, return that (richer info)
  const files = AUDIO_LIBRARY[mode].voice;
  const match = files.find((f) => f.url === url);
  if (match) return match;

  // Otherwise synthesize a minimal metadata object from the URL alone
  return {
    id: `${mode}-voice`,
    name: `Voz ${mode}`,
    description: '',
    duration: 0,
    format: 'ogg',
    url,
    category: 'voice',
    mode,
    tags: [],
  };
}

/**
 * Get all audio files for a specific mode
 */
export function getAudioFilesForMode(mode: AudioMode): AudioFileMetadata[] {
  const library = AUDIO_LIBRARY[mode];
  return [...library.ambience, ...library.voice];
}

/**
 * Get audio file by ID
 */
export function getAudioFileById(id: string): AudioFileMetadata | null {
  for (const mode of Object.keys(AUDIO_LIBRARY) as AudioMode[]) {
    const files = getAudioFilesForMode(mode);
    const found = files.find((f) => f.id === id);
    if (found) return found;
  }
  return null;
}

export default MODE_CONFIGS;